import { Button, Badge } from "@/components/primitives";
import {
	ArrowLeft,
	Printer,
	Download,
	Archive,
	ArchiveRestore,
	FileText,
} from "lucide-react";
import { handlePrint, handleSaveAsPDF } from "../utils";
import type { Reflection } from "../types";
import type { ReflectionAnalysisRef } from "@/components/ReflectionAnalysis";

interface ReflectionHeaderProps {
	reflection: Reflection;
	onBack: () => void;
	onArchive: () => void;
	onUnarchive: () => void;
	archiveLoading: boolean;
	reflectionAnalysisRef: React.RefObject<ReflectionAnalysisRef>;
	onIncludeAnalysisInPrint: (include: boolean) => void;
	onShowPrintDialog: (show: boolean) => void;
	onSetPendingPrintAction: (action: (() => void) | null) => void;
}

export function ReflectionHeader({
	reflection,
	onBack,
	onArchive,
	onUnarchive,
	archiveLoading,
	reflectionAnalysisRef,
	onIncludeAnalysisInPrint,
	onShowPrintDialog,
	onSetPendingPrintAction,
}: ReflectionHeaderProps) {
	const checkAnalysisExists = (): boolean => {
		return reflectionAnalysisRef.current?.getAnalysis() !== null;
	};

	const handlePrintClick = () => {
		handlePrint(
			checkAnalysisExists,
			onIncludeAnalysisInPrint,
			onShowPrintDialog,
			onSetPendingPrintAction,
			reflection.title,
		);
	};

	const handleSaveAsPDFClick = () => {
		handleSaveAsPDF(
			checkAnalysisExists,
			onIncludeAnalysisInPrint,
			onShowPrintDialog,
			onSetPendingPrintAction,
			reflection.title,
		);
	};
	return (
		<div className="mb-8 no-print">
			<div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
				<div className="flex flex-wrap items-center gap-2">
					{reflection.is_draft && (
						<Badge
							variant="outline"
							className="text-muted-foreground border-border"
						>
							Draft
						</Badge>
					)}
					{reflection.archived && (
						<Badge
							variant="outline"
							className="text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20"
						>
							Archived
						</Badge>
					)}
					{reflection.topic_tags?.map((tag) => (
						<Badge key={tag} variant="secondary">
							{tag}
						</Badge>
					))}
				</div>
				<div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={onBack}
						className="w-full sm:w-auto"
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back to Reflections
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={handlePrintClick}
						className="w-full sm:w-auto no-print"
					>
						<Printer className="h-4 w-4 mr-2" />
						Print
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={handleSaveAsPDFClick}
						className="w-full sm:w-auto no-print"
					>
						<Download className="h-4 w-4 mr-2" />
						Save as PDF
					</Button>
					{reflection.archived ? (
						<Button
							variant="outline"
							size="sm"
							onClick={onUnarchive}
							disabled={archiveLoading}
							className="w-full sm:w-auto"
						>
							<ArchiveRestore className="h-4 w-4 mr-2" />
							Unarchive
						</Button>
					) : (
						<Button
							variant="outline"
							size="sm"
							onClick={onArchive}
							disabled={archiveLoading}
							className="w-full sm:w-auto"
						>
							<Archive className="h-4 w-4 mr-2" />
							Archive
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
